/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { DeviceInfo } from '@amzn/vinyl-amzn'
import type { CodeData } from '@amzn/vinyl-amzn/cbl'
import { getUserAgentInfo, type UserAgentInfo } from '@amazon/vinyl-util'
import { clientApp } from '@/device/clientApp'
import { loadDeviceInfo } from '@/device/device'

/**
 * The OS as CBL wants it: a required string, so an unrecognized user agent (or
 * a recognized OS with no parseable version) reports `unknown` / the bare name
 * rather than interpolating `undefined` into the registration record.
 */
function formatOsVersion(os: UserAgentInfo['os']): string {
    if (!os) return 'unknown'
    return os.version ? `${os.name} ${os.version.str}` : os.name
}

export function createCodeData(deviceInfo: DeviceInfo): CodeData {
    const uA = getUserAgentInfo()
    return {
        app_name: clientApp.name,
        app_version: clientApp.version,
        device_model: 'Vinyl Test',
        device_serial: deviceInfo.deviceId,
        device_name: deviceInfo.deviceName,
        device_type: deviceInfo.deviceType,
        domain: 'Device',
        os_version: formatOsVersion(uA.os),
    }
}

export async function getCodeData(): Promise<CodeData> {
    return createCodeData(await loadDeviceInfo())
}
